import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { trackBookDemoClicked, trackDemoLinkClicked } from "@/utils/analytics";
import { useDemoAccess } from "@/components/DemoAccessModal";
import { useCampaignQuery } from "@/contexts/CampaignContext";
import { googleRegistrationUrl, withAttribution } from "@/utils/googleRegistrationAttribution";
import { appBookDemoUrl } from "@/utils/appLegal";
import { peekDemoUrl, tradeDemoUrl, withDemoCampaign } from "@/utils/peekDemo";

const DEMO_BASE_URL = "https://demo.bidmio.app/demo";
const REF_MAX_LENGTH = 32;

const LANG_MAP: Record<string, { lang: string; refTld: string }> = {
  sk: { lang: "sk", refTld: "sk" },
  cs: { lang: "cs", refTld: "cz" },
  da: { lang: "da", refTld: "dk" },
  de: { lang: "de", refTld: "de" },
  en: { lang: "en", refTld: "com" },
};

const sanitizeRef = (raw: string): string => {
  return raw
    .replace(/[^A-Za-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, REF_MAX_LENGTH)
    .replace(/-+$/g, "");
};

export const useRotatingCTA = (rotate = true) => {
  const { t, i18n } = useTranslation();
  const { requestDemoAccess } = useDemoAccess();
  const campaign = useCampaignQuery();
  const [ctaIndex, setCtaIndex] = useState(0);

  const rotatingCTAs = t("shared.cta.rotating", { returnObjects: true }) as string[];

  useEffect(() => {
    if (!rotate || rotatingCTAs.length < 2) return;
    const interval = setInterval(() => {
      setCtaIndex((prev) => (prev + 1) % rotatingCTAs.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [rotatingCTAs.length, rotate]);

  const getAppUrl = (extra?: string) =>
    googleRegistrationUrl(i18n.language, extra ? withAttribution(campaign, extra) : campaign);

  const getBookDemoUrl = () => appBookDemoUrl(i18n.language);

  const getPeekDemoUrl = () => peekDemoUrl(i18n.language, campaign);
  const getTradeDemoUrl = (industry: string) => tradeDemoUrl(i18n.language, industry, campaign);

  const buildDemoMeta = (refSuffix?: string) => {
    const cfg = LANG_MAP[i18n.language] || LANG_MAP.en;
    const rawRef = refSuffix ? `bidmio-${cfg.refTld}-${refSuffix}` : `bidmio-${cfg.refTld}`;
    const ref = sanitizeRef(rawRef);
    return { cfg, ref };
  };

  const getDemoUrl = (refSuffix?: string) => {
    const { cfg, ref } = buildDemoMeta(refSuffix);
    return withDemoCampaign(`${DEMO_BASE_URL}?lang=${cfg.lang}&ref=${ref}`, campaign);
  };

  const handleDemoClick = useCallback(
    (source: string, refSuffix?: string) => {
      const { cfg } = buildDemoMeta(refSuffix);
      trackDemoLinkClicked(source, cfg.lang);
    },
    [i18n.language]
  );

  const openDemoModal = useCallback(
    (source: string, refSuffix?: string) => (e?: React.MouseEvent) => {
      e?.preventDefault();
      requestDemoAccess({ source, refSuffix });
    },
    [requestDemoAccess]
  );

  const handleBookDemoClick = useCallback(
    (source: string) => (_e?: React.MouseEvent) => {
      const cfg = LANG_MAP[i18n.language] || LANG_MAP.en;
      trackBookDemoClicked(source, cfg.lang);
    },
    [i18n.language]
  );

  return {
    ctaIndex,
    rotatingCTAs,
    getAppUrl,
    getBookDemoUrl,
    getPeekDemoUrl,
    getTradeDemoUrl,
    getDemoUrl,
    handleDemoClick,
    handleBookDemoClick,
    openDemoModal,
    currentCTA: rotatingCTAs[ctaIndex],
  };
};
